import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type FC,
} from "react";
import type { Dayjs } from "dayjs";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import GeographicUserInsightsJs from "../../../shared/Dashboard/GeographicUserInsights/GeographicUserInsights";
import { Select } from "../../../shared";
import { ChartCard } from "../../../shared/Charts";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { DATE_RANGE } from "../../../../utils/Constants";
import useClientCountry from "../../../../hooks/useClientCountry";
import useCurrencySymbol from "../../../../hooks/useCurrencySymbol";
import { useAffiliateGeo } from "../../../../queries/affiliates/useAffiliateGeo";
import type { AffiliateGeoItem } from "../../../../queries/affiliates/useAffiliateGeo";
import type { AffiliateAnalyticsDataSource } from "../../../../queries/affiliates/analyticsDataSource";
import type { AffiliateKpisScope } from "../../affiliatesData";
import { useChartLocalFilters } from "../useChartLocalFilters";

import styles from "./AffiliatesByState.module.css";
import { getAdminNow } from "../../../../utils/dateService";

const CustomDatePicker = DatePickerJs as ComponentType<{
  dates: DateRange;
  setDates: (dates: DateRange) => void;
  getData: (queryString: string | null) => void;
  showQuickSelect?: boolean;
  showPresets?: boolean;
  startRange?: string;
}>;

const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<{
  data: Record<string, unknown>[];
  filename: string;
}>;

const GeographicUserInsights = GeographicUserInsightsJs as ComponentType<{
  mapId: string;
  country?: string;
  data: { name: string; abbr: string; amount: number }[];
  isDollars?: boolean;
  currencySymbol?: string;
}>;

type DateRange = [Dayjs, Dayjs];

type MetricKey =
  | "TOTAL_FTDS"
  | "TOTAL_USERS"
  | "FTD_RATE"
  | "TOTAL_OWED"
  | "TOTAL_REVENUE"
  | "AVERAGE_LTV";

type StateRow = {
  name: string;
  abbr: string;
} & Record<MetricKey, number>;

// BE gives only the 2-letter state code; use it for both the map key (abbr)
// and the label. FTD_RATE arrives as a 0–100 percentage — kept as a ratio
// here to match the mock's scale.
const geoToRow = (item: AffiliateGeoItem): StateRow => ({
  name: item.state,
  abbr: item.state,
  TOTAL_FTDS: item.metrics.TOTAL_FTDS ?? 0,
  TOTAL_USERS: item.metrics.TOTAL_USERS ?? 0,
  FTD_RATE: (item.metrics.FTD_RATE ?? 0) / 100,
  TOTAL_OWED: item.metrics.TOTAL_OWED ?? 0,
  TOTAL_REVENUE: item.metrics.TOTAL_REVENUE ?? 0,
  AVERAGE_LTV: item.metrics.AVERAGE_LTV ?? 0,
});

const CURRENCY_METRICS = new Set<MetricKey>([
  "TOTAL_OWED",
  "TOTAL_REVENUE",
  "AVERAGE_LTV",
]);

export type AffiliatesByStateProps = {
  scope?: AffiliateKpisScope;
  affiliateId?: string;
  // BE UPPER_SNAKE keys. Undefined → full catalogue. See TopFiveAffiliates
  // for the same pattern.
  visibleMetrics?: ReadonlyArray<string>;
  dataSource?: AffiliateAnalyticsDataSource;
  // Cross-cut filters lifted from the KPIs tab's global filter dropdown.
  affiliate?: string;
  promoCode?: string;
  // Options for the card's own selects. Codes cover the cross-cut
  // affiliate; a chart-local affiliate pick fetches its own code list.
  affiliateOptions?: ReadonlyArray<{ value: string; label: string }>;
  isLoadingAffiliates?: boolean;
  codeOptions?: ReadonlyArray<{ value: string; label: string }>;
  isLoadingCodes?: boolean;
  // False → render empty without fetching (previews of an affiliate that
  // doesn't exist yet).
  queryEnabled?: boolean;
};

const AffiliatesByState: FC<AffiliatesByStateProps> = ({
  scope = "module",
  affiliateId,
  visibleMetrics,
  dataSource = "operator",
  affiliate,
  promoCode,
  affiliateOptions,
  isLoadingAffiliates = false,
  codeOptions,
  isLoadingCodes = false,
  queryEnabled = true,
}) => {
  const currencySymbol = useCurrencySymbol() ?? "$";
  const clientCountry = useClientCountry();
  const [dates, setDates] = useState<DateRange>(() => [
    getAdminNow().startOf("month"),
    getAdminNow().endOf("day"),
  ]);
  const [metric, setMetric] = useState<MetricKey>("TOTAL_FTDS");
  const {
    selectedAffiliate,
    setSelectedAffiliate,
    selectedCode,
    setSelectedCode,
    filters,
    codeSelect,
  } = useChartLocalFilters({ affiliate, promoCode, codeOptions, isLoadingCodes });

  const query = useAffiliateGeo(
    {
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    },
    metric,
    affiliateId,
    filters,
    dataSource,
    { enabled: queryEnabled },
  );
  const rows = useMemo(() => (query.data ?? []).map(geoToRow), [query.data]);

  // Built inside the memo: AppStrings values are reassigned when the
  // localized strings load, so a module-level const would freeze English.
  const metricOptions = useMemo(() => {
    const allMetricOptions: { value: MetricKey; label: string }[] = [
      { value: "TOTAL_FTDS", label: AppStrings.TOTAL_FTDS },
      { value: "TOTAL_USERS", label: AppStrings.TOTAL_USERS },
      { value: "FTD_RATE", label: AppStrings.FTD_RATE },
      { value: "TOTAL_OWED", label: AppStrings.TOTAL_OWED },
      { value: "TOTAL_REVENUE", label: AppStrings.TOTAL_REVENUE },
      { value: "AVERAGE_LTV", label: AppStrings.AVERAGE_LTV },
    ];
    return visibleMetrics
      ? allMetricOptions.filter((opt) => visibleMetrics.includes(opt.value))
      : allMetricOptions;
  }, [visibleMetrics]);

  useEffect(() => {
    if (metricOptions.length === 0) return;
    if (!metricOptions.some((opt) => opt.value === metric)) {
      setMetric(metricOptions[0].value);
    }
  }, [metricOptions, metric]);

  const mapData = useMemo(
    () =>
      rows.map((row) => ({
        name: row.name,
        abbr: row.abbr,
        amount: row[metric],
      })),
    [rows, metric],
  );

  const exportRows = useMemo(
    () =>
      rows.map((row) => ({
        state: row.name,
        abbreviation: row.abbr,
        total_ftds: row.TOTAL_FTDS,
        total_users: row.TOTAL_USERS,
        ftd_rate: row.FTD_RATE * 100,
        total_owed: row.TOTAL_OWED,
        total_revenue: row.TOTAL_REVENUE,
        average_ltv: row.AVERAGE_LTV,
      })),
    [rows],
  );

  const getData = useCallback((_queryString: string | null) => {
    // Date changes flow through `dates` -> the query key.
  }, []);

  return (
    <ChartCard
      ariaLabel={AppStrings.AFFILIATES_BY_STATE}
      headerExtra={
        <Select<MetricKey>
          className={styles.metricSelect}
          value={metric}
          options={metricOptions}
          onChange={(value) => setMetric(value as MetricKey)}
          aria-label={AppStrings.METRIC}
        />
      }
      bodyClassName={styles.mapContainer}
      controls={
        <>
          {scope === "module" && (
            <Select<string>
              className={styles.filterSelect}
              placeholder={AppStrings.ALL_AFFILIATES}
              value={selectedAffiliate}
              onChange={(value) => setSelectedAffiliate(value)}
              allowClear
              showSearch
              optionFilterProp="label"
              options={affiliateOptions ? [...affiliateOptions] : []}
              loading={isLoadingAffiliates}
            />
          )}
          <Select<string>
            className={styles.filterSelect}
            placeholder={AppStrings.ALL_CODES}
            value={selectedCode}
            onChange={(value) => setSelectedCode(value)}
            allowClear
            showSearch
            optionFilterProp="label"
            options={codeSelect.options}
            loading={codeSelect.loading}
            disabled={codeSelect.disabled}
          />
          <CustomDatePicker
            getData={getData}
            dates={dates}
            setDates={setDates}
            showPresets
            startRange={DATE_RANGE.CURRENT_MONTH}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename="affiliates-by-state.xlsx"
          />
        </>
      }
    >
      <GeographicUserInsights
        mapId="affiliates-by-state"
        country={clientCountry}
        data={mapData}
        isDollars={CURRENCY_METRICS.has(metric)}
        currencySymbol={currencySymbol}
      />
    </ChartCard>
  );
};

export default AffiliatesByState;
